import { motion } from "framer-motion";
import { ShieldCheck, Mail, Phone, MapPin } from "lucide-react";
import { OFFICES, CONTACT } from "@/data/services";

const SECTIONS = [
  {
    h: "1. Introduction",
    p: ["ImmiLaw.VN (\"we\", \"us\", \"our\") is a private immigration and legal consultancy assisting individuals and businesses with Vietnam visa applications, business registration, visa extensions and overstay reports. This Privacy Policy explains how we collect, use, store and protect your personal data when you use our website and services. ImmiLaw.VN is not an official Vietnam government website."],
  },
  {
    h: "2. Information We Collect",
    list: [
      "Account information: your name, email address and phone number provided at registration.",
      "Identity & passport data: information extracted from the passport bio-data page you upload (full name, passport number, nationality, date of birth, sex, place of birth, issue and expiry dates) and the passport image itself.",
      "Application details: the information you enter into our application forms for the relevant service.",
      "Communications: messages you send to our team through your dashboard.",
      "Payment information: transaction identifiers and amounts. Card details are processed directly by our payment provider (PayPal) and are never stored on our servers.",
    ],
  },
  {
    h: "3. How We Use Your Information",
    list: [
      "To create and manage your account and verify your email address.",
      "To prepare, review and lodge your immigration applications in accordance with Vietnamese regulations.",
      "To automatically pre-fill your application forms from your uploaded passport image.",
      "To generate quotations, receipts and application documents.",
      "To communicate with you about your applications and provide customer support.",
      "To process payments for the services you request.",
    ],
  },
  {
    h: "4. Automated Passport Data Extraction",
    p: ["When you upload a photo of your passport bio-data page, we use a secure artificial-intelligence service to read the printed details so we can pre-fill your form. You may review and correct all extracted fields before submitting. Uploaded images are stored securely and used only to process your application."],
  },
  {
    h: "5. Third-Party Services",
    list: [
      "Payments are handled by PayPal, subject to PayPal's own privacy policy.",
      "Transactional emails (verification, welcome, application and receipt notifications) are delivered through our email provider.",
      "AI-assisted passport reading and our website assistant are provided by trusted processing partners under confidentiality obligations.",
    ],
  },
  {
    h: "6. Data Storage & Security",
    p: ["We apply appropriate technical and organisational measures to protect your personal data, including encrypted transport, access controls and secure cloud storage. Passwords are stored using industry-standard hashing and are never accessible in plain text. While no method of transmission over the internet is fully secure, we strive to protect your information at all times."],
  },
  {
    h: "7. Data Retention",
    p: ["We retain your personal data only for as long as necessary to provide our services, comply with legal and regulatory obligations, resolve disputes and enforce our agreements. You may request deletion of your account and associated data at any time, subject to any records we are required to keep by law."],
  },
  {
    h: "8. Your Rights",
    p: ["Subject to applicable law, you have the right to access, correct, update or delete your personal data, to object to or restrict certain processing, and to withdraw consent where processing is based on consent. To exercise these rights, please contact us using the details below."],
  },
  {
    h: "9. Cookies",
    p: ["We use only essential storage on your device (such as your login session and theme preference) to operate the website. We do not use third-party advertising cookies."],
  },
  {
    h: "10. Changes to This Policy",
    p: ["We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. Your continued use of our services after changes take effect constitutes acceptance of the revised policy."],
  },
];

export default function Privacy() {
  return (
    <div>
      <section className="bg-brand-navy text-white grain">
        <div className="max-w-4xl mx-auto px-6 py-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs font-semibold text-brand-gold mb-5">
              <ShieldCheck className="w-3.5 h-3.5" /> Your privacy matters
            </div>
            <h1 className="font-serif text-5xl md:text-6xl tracking-tight">Privacy &amp; Policy</h1>
            <p className="mt-4 text-slate-300 text-lg">How ImmiLaw.VN collects, uses and protects your personal information.</p>
            <p className="mt-2 text-sm text-slate-400">Last updated: June 2026</p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="prose-none space-y-10">
          {SECTIONS.map((s) => (
            <div key={s.h} data-testid={`privacy-section`}>
              <h2 className="font-serif text-2xl md:text-3xl text-brand-navy tracking-tight">{s.h}</h2>
              {s.p && s.p.map((para, i) => <p key={i} className="mt-3 text-slate-600 leading-relaxed">{para}</p>)}
              {s.list && (
                <ul className="mt-3 space-y-2.5">
                  {s.list.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-slate-600 leading-relaxed">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-2.5 shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-8 mt-12" data-testid="privacy-contact">
            <h2 className="font-serif text-2xl text-brand-navy tracking-tight">11. Contact Us</h2>
            <p className="mt-3 text-slate-600">For any privacy questions or requests, please contact us:</p>
            <div className="mt-5 grid sm:grid-cols-2 gap-4 text-sm">
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2.5 text-slate-700 hover:text-brand-red transition-colors"><Mail className="w-4 h-4 text-brand-red" /> {CONTACT.email}</a>
              <a href={`tel:${CONTACT.phone}`} className="flex items-center gap-2.5 text-slate-700 hover:text-brand-red transition-colors"><Phone className="w-4 h-4 text-brand-red" /> {CONTACT.phoneDisplay}</a>
              {OFFICES.map((o) => (
                <div key={o.city} className="flex items-start gap-2.5 text-slate-700"><MapPin className="w-4 h-4 text-brand-red shrink-0 mt-0.5" /><span><strong>{o.city}:</strong> {o.address}</span></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
